import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { useFieldProps } from '../lib/field-context';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Force the error styling without a surrounding `<FormField>`. */
  invalid?: boolean;
}

/**
 * A text input. Inside a `<FormField>` it picks up the field's id, its
 * `aria-describedby` and its invalid state automatically; explicit props still
 * win. Outside one it is just a styled `<input>`.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, id, invalid, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  // An explicit aria-invalid always wins over the field's; `undefined` lets the
  // surrounding FormField decide.
  const resolvedInvalid: boolean | undefined =
    ariaInvalid === undefined
      ? invalid === true
        ? true
        : undefined
      : ariaInvalid !== false && ariaInvalid !== 'false';

  const fieldProps = useFieldProps({
    id,
    'aria-describedby': describedBy,
    'aria-invalid': resolvedInvalid,
  });

  return (
    <input
      ref={ref}
      className={cn(
        'block w-full rounded-base border border-line-strong bg-ink/55 px-3.5 py-2.5',
        'text-[0.95rem] leading-normal text-text placeholder:text-muted',
        'transition-[background-color,border-color,box-shadow] duration-150 ease-wend',
        'hover:border-white/25',
        'focus-visible:border-brand-a focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
        'disabled:cursor-not-allowed disabled:opacity-55',
        'aria-[invalid=true]:border-bad aria-[invalid=true]:focus-visible:outline-bad',
        className,
      )}
      {...fieldProps}
      {...rest}
    />
  );
});
